/**
 * CreateObjectSingleton
 *
 * Lightweight global singleton registry (per-string key).
 * Exposes per-key helpers: `get()`, `set(value)`, `delete()`, `has()`.
 */
export function createObjectSingleton<T = any>(key: string) {
  // Use `globalThis` as the shared container so the registry survives
  // across module boundaries, multiple bundles, and hot-reloads.
  const GWindow: any = globalThis

  // Ensure the internal registry object exists on the provided `store`
  // (defaults to `globalThis`). Returns the registry object at `store[key]`.
  const getWindowHandler = (key: string, store?: any, value?: any) => {
    store ??= GWindow
    if (!store[key]) {
      store[key] = value ?? {}
    }
    return store[key]
  }

  // Internal storage for entries. This object holds actual values and is
  // captured by the Proxy so methods can interact with this underlying map.
  const handlerActions: Record<string, T> = {}

  class ProxyMapSingleton {
    constructor() {
      // The Proxy intercepts property access so we can expose the class
      // methods and derived properties (like `size`) while forwarding any
      // unknown properties to the underlying instance.
      return new Proxy(this, {
        get: (target: any, prop: string | symbol) => {
          if (prop === 'set') return target.set.bind(target)
          if (prop === 'get') return target.get.bind(target)
          if (prop === 'delete') return target.delete.bind(target)
          if (prop === 'has') return target.has.bind(target)
          if (prop === 'clear') return target.clear.bind(target)
          if (prop === 'keys') return target.keys.bind(target)
          if (prop === 'values') return target.values.bind(target)
          if (prop === 'entries') return target.entries.bind(target)
          // Expose a read-only `size` derived from the internal storage.
          if (prop === 'size') return Object.keys(handlerActions).length
          return (target as any)[prop] // other properties (if any)
        }
      })
    }

    // Set a value for `key` in the shared registry.
    set(key: string, value: T) {
      handlerActions[key] = value
    }

    // Get the value for `key` from the shared registry.
    get(key: string): T | undefined {
      return handlerActions[key]
    }

    // Delete an entry by `key`. Returns `true` if removed.
    delete(key: string) {
      if (key in handlerActions) {
        delete handlerActions[key]
        return true
      }
      return false
    }

    // Check whether `key` exists in the registry.
    has(key: string) {
      return key in handlerActions
    }

    // Remove all entries from the registry.
    clear() {
      Object.keys(handlerActions).forEach((key) => delete handlerActions[key])
    }

    // Return an array of stored keys.
    keys() {
      return Object.keys(handlerActions)
    }

    // Return an array of stored values.
    values() {
      return Object.values(handlerActions)
    }

    // Return an array of [key, value] pairs.
    entries() {
      return Object.entries(handlerActions)
    }
  }

  // Create or reuse the shared proxy-backed registry stored on `globalThis`.
  const HandlerAction: any = getWindowHandler('z_y1s_651_ps', GWindow, new ProxyMapSingleton())

  // Return a compact per-key API that operates against the shared registry.
  // Note: This returns only the per-key helpers; other registry-level helpers
  // (like `size`, `keys`, etc.) are available on the shared proxy itself.
  return {
    get: () => HandlerAction.get(key),
    set: (value: T) => HandlerAction.set(key, value),
    delete: () => HandlerAction.delete(key),
    has: () => HandlerAction.has(key)
  }
}
export default createObjectSingleton
